import * as L from 'leaflet';
import Mustache from 'mustache';
import noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';
import tippy, { followCursor } from 'tippy.js';

import { svgToGeoJson } from './svgtogeojson';
import GTMEvents from './gtm-events';
export default class Navigator {
	constructor() {
		if (themeVars.hasOwnProperty('navigator') === false) {
			return;
		}

		if (history.scrollRestoration) {
			history.scrollRestoration = 'manual';
		} else {
			window.onbeforeunload = function () {
				window.scrollTo(0, 0);
			};
		}

		this.navigator = document.querySelector('[data-navigator="element"]');

		if (null === this.navigator) {
			return;
		}

		this.state = this.getDefaultState();
		this.filter = this.navigator.querySelector('[data-navigator-filter="element"]');

		this.placements = themeVars.navigator.hasOwnProperty('placements') ? themeVars.navigator.placements : {};

		this.apartments = themeVars.navigator.hasOwnProperty('apartments') ? themeVars.navigator.apartments : {};

		this.state.filter.active = themeVars.navigator.hasOwnProperty('filters') ? themeVars.navigator.filters : {};

		this.activeFilters = Object.keys(this.state.filter.active).length;

		this.events = {};
		this.jsonLayer;

		this.svg = document.getElementById('apartments');
		this.refreshSize();

		this.initTabs();
		this.initMap();
		this.initTable();
		this.initFilters();

		window.addEventListener(
			'resize',
			this.throttle(() => {
				this.refreshSize();
				this.map.remove();
				this.initMap();
			}, 500),
		);
	}

	throttle(fn, wait) {
		var time = Date.now();
		return function () {
			if (time + wait - Date.now() < 0) {
				fn();
				time = Date.now();
			}
		};
	}

	refreshSize() {
		const windowWidth = window.innerWidth + 200;
		const windowHeight = window.innerHeight + 200;

		const imageRatio = this.svg.getAttribute('width') / this.svg.getAttribute('height');
		const screenRatio = windowWidth / windowHeight;

		this.width =
			screenRatio >= imageRatio ? windowWidth : parseInt(this.svg.getAttribute('width')) * (windowHeight / parseInt(this.svg.getAttribute('height')));
		this.height =
			screenRatio < imageRatio ? windowHeight : parseInt(this.svg.getAttribute('height')) * (windowWidth / parseInt(this.svg.getAttribute('width')));
	}

	getDefaultState() {
		const defaultState = {
			view: 'table',
			filter: {
				open: false,
				active: {},
			},
			sort: {
				attribute: 'availability',
				direction: 'asc',
			},
		};

		return defaultState;
	}

	initTabs() {
		const triggers = this.navigator.querySelectorAll('[data-navigator-tabs="trigger"]');
		const targets = this.navigator.querySelectorAll('[data-navigator-tabs="target"]');

		this.events.refresh = new CustomEvent('tabsRefresh');

		triggers.forEach((trigger) => {
			if (trigger.getAttribute('data-id') === 'visual' && trigger.classList.contains('active')) {
				this.state.view = 'visual';
			}

			trigger.addEventListener('click', () => {
				const id = trigger.getAttribute('data-id');
				const link = trigger.getAttribute('data-link');
				this.state.view = id;

				triggers.forEach((trigger) => trigger.classList.remove('active'));
				trigger.classList.add('active');

				targets.forEach((target) => {
					const tid = target.getAttribute('data-id');
					tid === id ? target.classList.add('active') : target.classList.remove('active');
				});

				window.history.replaceState({}, '', link);
				this.navigator.dispatchEvent(this.events.refresh);
			});
		});
	}

	refreshTabs() {
		this.map.invalidateSize();

		if (this.state.view === 'visual') {
			document.body.scrollTop = 0;
			document.documentElement.scrollTop = 0;
			disableBodyScroll(this.filter);
		} else {
			clearAllBodyScrollLocks();
		}
	}

	initMap() {
		this.canvas = this.navigator.querySelector('[data-navigator-visual="element"]');

		this.bounds = [
			[0, 0],
			[this.height, this.width],
		];

		this.map = L.map(this.canvas, {
			crs: L.CRS.Simple,
			maxBounds: this.bounds,
			maxBoundsViscosity: 1.0,
			maxZoom: 1,
			zoomControl: false,
			zoomDelta: 0.25,
			zoomSnap: 0.25,
			bounceAtZoomLimits: false,
			attributionControl: false,
		});

		this.map.getRenderer(this.map).options.padding = 100;

		const image = L.imageOverlay(this.canvas.getAttribute('data-url'), this.bounds).addTo(this.map);

		const zoom = L.control.zoom({ position: 'bottomright' }).addTo(this.map);

		this.map.fitBounds(this.bounds);
		this.addMapLayer();
		this.refreshTabs();

		setTimeout(() => {
			this.map.invalidateSize();
		}, 250);

		const resizeObserver = new ResizeObserver(() => {
			this.map.invalidateSize();
		});

		resizeObserver.observe(this.canvas);

		this.navigator.addEventListener('filtersChanged', (e) => {
			this.addMapLayer();
		});

		this.navigator.addEventListener('tabsRefresh', (e) => {
			this.refreshTabs();
		});
	}

	addMapLayer() {
		this.activeFilters = Object.keys(this.state.filter.active).length;

		if (typeof this.jsonLayer !== 'undefined' && this.map.hasLayer(this.jsonLayer)) {
			this.map.removeLayer(this.jsonLayer);
		}

		const geoJson = svgToGeoJson(
			[
				[this.height, this.width],
				[0, 0],
			],
			this.svg,
			5,
			['id'],
		);

		this.jsonLayer = L.geoJson(geoJson, {
			style: (feature) => {
				const isActive = this.filterPlacement(feature);

				return {
					fillColor: '#000000',
					color: '#000000',
					fillOpacity: isActive === true ? 0 : 0.6,
					opacity: 0,
					weight: 2,
					className: isActive === true ? 'active' : 'not-active',
				};
			},
			onEachFeature: this.eachFeature.bind(this),
		}).addTo(this.map);
	}

	filterPlacement(feature) {
		this.activeFilters = Object.keys(this.state.filter.active).length;

		if (this.placements.length < 1) {
			return false;
		}

		if (this.activeFilters < 1) {
			return true;
		}

		const id = feature.properties.id.replace('-', '_');

		if (this.placements.hasOwnProperty(id)) {
			const placement = this.placements[id];
			let validApartments = [];

			placement.apartments.forEach((apartment) => {
				let apartmentChecked = true;

				if (
					this.state.filter.active.hasOwnProperty('availability') &&
					this.state.filter.active.availability.indexOf(apartment.availability) === -1 &&
					apartmentChecked === true
				) {
					apartmentChecked = false;
				}

				if (
					this.state.filter.active.hasOwnProperty('layout') &&
					this.state.filter.active.layout.indexOf(apartment.layout_id) === -1 &&
					apartmentChecked === true
				) {
					apartmentChecked = false;
				}

				if (this.state.filter.active.hasOwnProperty('price') && apartmentChecked === true) {
					let between = this.state.filter.active.price[0] <= apartment.price && apartment.price <= this.state.filter.active.price[1];

					if (between === false) {
						apartmentChecked = false;
					}
				}

				if (this.state.filter.active.hasOwnProperty('space') && apartmentChecked === true) {
					let between = this.state.filter.active.space[0] <= apartment.space && apartment.space <= this.state.filter.active.space[1];

					if (between === false) {
						apartmentChecked = false;
					}
				}

				if (apartmentChecked === true) {
					validApartments.push(apartment.layout);
				}
			});

			if (validApartments.length < 1) {
				return false;
			}

			if (this.state.filter.active.hasOwnProperty('floor') && this.state.filter.active.floor.indexOf(placement.floor_id) === -1) {
				return false;
			}

			if (this.state.filter.active.hasOwnProperty('block') && this.state.filter.active.block.indexOf(placement.block_id) === -1) {
				return false;
			}

			return true;
		}

		return false;
	}

	filterApartment(apartment) {
		this.activeFilters = Object.keys(this.state.filter.active).length;

		if (this.activeFilters < 1) {
			return true;
		}

		if (this.state.filter.active.hasOwnProperty('availability') && this.state.filter.active.availability.indexOf(apartment.availability) === -1) {
			return false;
		}

		if (this.state.filter.active.hasOwnProperty('layout') && this.state.filter.active.layout.indexOf(apartment.layout_id) === -1) {
			return false;
		}

		if (this.state.filter.active.hasOwnProperty('price')) {
			let between = this.state.filter.active.price[0] <= apartment.price && apartment.price <= this.state.filter.active.price[1];

			if (between === false) {
				return false;
			}
		}

		if (this.state.filter.active.hasOwnProperty('space')) {
			let between = this.state.filter.active.space[0] <= apartment.total_space && apartment.total_space <= this.state.filter.active.space[1];

			if (between === false) {
				return false;
			}
		}

		if (this.state.filter.active.hasOwnProperty('floor') && this.state.filter.active.floor.indexOf(apartment.floor_id) === -1) {
			return false;
		}

		if (this.state.filter.active.hasOwnProperty('block') && this.state.filter.active.block.indexOf(apartment.block_id) === -1) {
			return false;
		}

		return true;
	}

	eachFeature(feature, layer) {
		let self = this;
		const isActive = this.filterPlacement(feature);

		if (isActive !== true) {
			return;
		}

		layer.bindPopup(this.popupTemplate(feature.properties.id.replace('-', '_')), {
			closeButton: false,
			autoPanPadding: [50, 50],
			maxWidth: 400,
		});

		layer.on({
			mouseover: () => {
				layer.setStyle({
					fillColor: '#5312EB',
					color: '#5312EB',
					fillOpacity: 0.5,
					opacity: 1,
				});

				if (!L.Browser.opera && !L.Browser.edge) {
					layer.bringToFront();
				}
			},
			mouseout: (e) => {
				if (layer.isPopupOpen() === false) {
					self.jsonLayer.resetStyle(e.target);
				}
			},
			click: (e) => {
				if (window.innerWidth > 768) {
					self.map.fitBounds(e.target.getBounds());
				}
			},
			popupopen: (e) => {
				layer.setStyle({
					fillColor: '#5312EB',
					color: '#5312EB',
					fillOpacity: 0.5,
					opacity: 1,
				});
			},
			popupclose: (e) => {
				self.jsonLayer.resetStyle(e.target);
			},
		});
	}

	popupTemplate(id) {
		if (this.placements.hasOwnProperty(id) === false) {
			return '';
		}

		const placement = this.placements[id];

		return Mustache.render(this.navigator.querySelector('#popup-template').innerHTML, placement);
	}

	initFilters() {
		this.filters = {};
		this.activeFilters = Object.keys(this.state.filter.active).length;

		this.events.change = new CustomEvent('filtersChanged');
		this.events.reset = new CustomEvent('filtersReset');

		this.filters.count = this.navigator.querySelector('[data-navigator-filter="count"]');

		this.filters.trigger = this.navigator.querySelector('[data-navigator-filter="trigger"]');

		this.filters.trigger.addEventListener('click', (e) => {
			this.toggleFilters();
		});

		this.filters.close = this.navigator.querySelector('[data-navigator-filter="close"]');

		this.filters.close.addEventListener('click', (e) => {
			this.toggleFilters();
		});

		this.filters.reset = this.navigator.querySelector('[data-navigator-filter="reset"]');

		this.filters.reset.addEventListener('click', (e) => {
			this.resetFilters();
		});

		this.filters.items = this.navigator.querySelectorAll('[data-filter]');
		this.filters.items.forEach((filter) => {
			const type = filter.getAttribute('data-filter');
			const name = filter.getAttribute('data-filter-name');

			if (type === 'checkbox') {
				const inputs = filter.querySelectorAll('input[type="checkbox"]');

				inputs.forEach((input) => {
					if (this.state.filter.active.hasOwnProperty(name) && this.state.filter.active[name].indexOf(parseInt(input.value)) !== -1) {
						input.checked = true;
					}

					input.addEventListener('change', (e) => {
						this.state.filter.active[name] = [];

						inputs.forEach((input) => {
							if (input.checked === true) {
								let value = parseInt(input.value) || input.value;

								this.state.filter.active[name].push(value);
							}
						});

						if (this.state.filter.active[name].length < 1) {
							delete this.state.filter.active[name];
						}

						this.navigator.dispatchEvent(this.events.change);
					});

					this.navigator.addEventListener('filtersReset', (e) => {
						input.checked = false;
					});
				});
			}

			if (type === 'range') {
				const element = filter.querySelector('[data-range]');
				const range = JSON.parse(element.getAttribute('data-range'));

				if (range.hasOwnProperty('min') && range.hasOwnProperty('max') && range.hasOwnProperty('step')) {
					const slider = noUiSlider.create(element, {
						start: [range.min, range.max],
						connect: true,
						step: range.step,
						margin: range.step,
						range: {
							min: range.min,
							max: range.max,
						},
						format: wNumb({
							decimals: 0,
							thousand: ' ',
							suffix: ' ' + range.unit,
						}),
					});

					const snapValues = [filter.querySelector('[data-range-value="lower"]'), filter.querySelector('[data-range-value="upper"]')];

					slider.on('update', function (values, handle) {
						snapValues[handle].innerHTML = values[handle];
					});

					slider.on('change', (value, handle, unencoded) => {
						if (unencoded[0] === range.min && unencoded[1] === range.max) {
							delete this.state.filter.active[name];
						} else {
							this.state.filter.active[name] = unencoded;
						}

						this.navigator.dispatchEvent(this.events.change);
					});

					//this.mergeTooltips(element, 100, ' - ');

					this.navigator.addEventListener('filtersReset', (e) => {
						slider.reset();
					});
				}
			}

			if (type === 'price') {
				const element = filter.querySelector('[data-range]');
				const range = JSON.parse(element.getAttribute('data-range'));
				const rate = range.max / range.max_eur;

				if (range.hasOwnProperty('min') && range.hasOwnProperty('max') && range.hasOwnProperty('step')) {
					const slider = noUiSlider.create(element, {
						start: [range.min, range.max],
						connect: true,
						step: range.step,
						margin: range.step,
						range: {
							min: range.min,
							max: range.max,
						},
					});

					const snapValues = [filter.querySelector('[data-range-value="lower"]'), filter.querySelector('[data-range-value="upper"]')];

					slider.on('update', function (values, handle) {
						const value = parseInt(values[handle]);
						const format = wNumb({
							decimals: 0,
							thousand: ' ',
						});

						const czk = format.to(value);
						const eur = format.to(value / rate);

						snapValues[handle].innerHTML = czk + ' Kč / ' + eur + ' €';
					});

					slider.on('change', (value, handle, unencoded) => {
						if (unencoded[0] === range.min && unencoded[1] === range.max) {
							delete this.state.filter.active[name];
						} else {
							this.state.filter.active[name] = unencoded;
						}

						this.navigator.dispatchEvent(this.events.change);
					});

					//this.mergeTooltips(element, 100, ' - ');

					this.navigator.addEventListener('filtersReset', (e) => {
						slider.reset();
					});
				}
			}
		});

		this.navigator.addEventListener('filtersChanged', (e) => {
			this.activeFilters = Object.keys(this.state.filter.active).length;
			this.filters.count.innerHTML = this.activeFilters;

			if (this.activeFilters > 0) {
				this.filters.trigger.classList.add('active');
			} else {
				this.filters.trigger.classList.remove('active');
			}
		});

		this.navigator.dispatchEvent(this.events.change);
	}

	resetFilters() {
		this.state.filter.active = {};

		this.navigator.dispatchEvent(this.events.reset);
		this.navigator.dispatchEvent(this.events.change);
	}

	toggleFilters() {
		if (this.state.filter.open === false) {
			this.openFilters();
			this.filters.trigger._tippy.setContent(this.filters.trigger.getAttribute('data-tooltip-close'));
			this.filters.trigger._tippy.hide();
		} else {
			this.closeFilters();
			this.filters.trigger._tippy.setContent(this.filters.trigger.getAttribute('data-tooltip-open'));
			this.filters.trigger._tippy.hide();
		}
	}

	openFilters() {
		this.state.filter.open = true;
		this.navigator.classList.add('filters-opened');
		disableBodyScroll(document.querySelector('[data-navigator-filter="canvas"]'));
	}

	closeFilters() {
		this.state.filter.open = false;
		this.navigator.classList.remove('filters-opened');
		clearAllBodyScrollLocks();
	}

	initTable() {
		this.table = this.navigator.querySelector('[data-navigator-table="element"]');
		this.table.innerHTML = this.renderTable();

		this.navigator.addEventListener('filtersChanged', (e) => {
			this.table.innerHTML = this.renderTable();
			this.addTableEvents();
		});
	}

	renderTable() {
		let sortedApartments = this.apartments.sort((a, b) => {
			const attribute = this.state.sort.attribute;
			const direction = this.state.sort.direction;

			if (attribute === 'price' || attribute === 'number') {
				if (direction === 'asc') {
					return a[attribute] - b[attribute];
				} else {
					return b[attribute] - a[attribute];
				}
			}

			if (attribute === 'space') {
				if (direction === 'asc') {
					return a['total_space'] - b['total_space'];
				} else {
					return b['total_space'] - a['total_space'];
				}
			}

			if (attribute === 'availability') {
				const rank = {
					available: 0,
					'coming-soon': 1,
					'not-available': 2,
				};

				const aRank = rank[a.availability] ?? 99;
				const bRank = rank[b.availability] ?? 99;

				if (aRank === bRank) {
					if (a.availability === 'not-available') {
						if (direction === 'asc') {
							return a.number - b.number;
						} else {
							return b.number - a.number;
						}
					} else {
						const parseDate = (str) => {
							const [day, month, year] = str.split('/').map(Number);
							return new Date(year, month - 1, day);
						};

						const aDate = parseDate(a.availability_date);
						const bDate = parseDate(b.availability_date);

						if (direction === 'asc') {
							return aDate - bDate;
						} else {
							return bDate - aDate;
						}
					}
				}

				if (direction === 'asc') {
					return aRank - bRank;
				} else {
					return bRank - aRank;
				}
			}
		});

		let filteredApartments = sortedApartments.filter((apartment) => {
			return this.filterApartment(apartment);
		});

		const data = {
			apartments: filteredApartments,
		};

		if (filteredApartments < 1) {
			data['noResults'] = true;
		}

		return Mustache.render(this.navigator.querySelector('#table-template').innerHTML, data);
	}

	addTableEvents() {
		const sorts = this.table.querySelectorAll('[data-sort]');

		new GTMEvents(document.querySelector('[data-navigator-table="element"]')).download();

		tippy('[data-navigator-table="element"] [data-tippy-content]', {
			followCursor: true,
			plugins: [followCursor],
			animation: true,
			touch: false,
			arrow: false,
		});

		sorts.forEach((sort) => {
			const attribute = sort.getAttribute('data-sort');

			if (this.state.sort.attribute === attribute) {
				sort.classList.add('active');
				sort.classList.add(this.state.sort.direction);
			}

			sort.addEventListener('click', (e) => {
				if (this.state.sort.attribute !== attribute) {
					this.state.sort.direction = 'asc';
				} else {
					this.state.sort.direction = this.state.sort.direction === 'asc' ? 'desc' : 'asc';
				}

				this.state.sort.attribute = attribute;

				this.table.innerHTML = this.renderTable();
				this.addTableEvents();
			});
		});
	}

	mergeTooltips(slider, threshold, separator) {
		var textIsRtl = getComputedStyle(slider).direction === 'rtl';
		var isRtl = slider.noUiSlider.options.direction === 'rtl';
		var isVertical = slider.noUiSlider.options.orientation === 'vertical';
		var tooltips = slider.noUiSlider.getTooltips();
		var origins = slider.noUiSlider.getOrigins();

		// Move tooltips into the origin element. The default stylesheet handles this.
		tooltips.forEach(function (tooltip, index) {
			if (tooltip) {
				origins[index].appendChild(tooltip);
			}
		});

		slider.noUiSlider.on('update', function (values, handle, unencoded, tap, positions) {
			var pools = [[]];
			var poolPositions = [[]];
			var poolValues = [[]];
			var atPool = 0;

			// Assign the first tooltip to the first pool, if the tooltip is configured
			if (tooltips[0]) {
				pools[0][0] = 0;
				poolPositions[0][0] = positions[0];
				poolValues[0][0] = values[0];
			}

			for (var i = 1; i < positions.length; i++) {
				if (!tooltips[i] || positions[i] - positions[i - 1] > threshold) {
					atPool++;
					pools[atPool] = [];
					poolValues[atPool] = [];
					poolPositions[atPool] = [];
				}

				if (tooltips[i]) {
					pools[atPool].push(i);
					poolValues[atPool].push(values[i]);
					poolPositions[atPool].push(positions[i]);
				}
			}

			pools.forEach(function (pool, poolIndex) {
				var handlesInPool = pool.length;

				for (var j = 0; j < handlesInPool; j++) {
					var handleNumber = pool[j];

					if (j === handlesInPool - 1) {
						var offset = 0;

						poolPositions[poolIndex].forEach(function (value) {
							offset += 1000 - value;
						});

						var direction = isVertical ? 'bottom' : 'right';
						var last = isRtl ? 0 : handlesInPool - 1;
						var lastOffset = 1000 - poolPositions[poolIndex][last];
						offset = (textIsRtl && !isVertical ? 100 : 0) + offset / handlesInPool - lastOffset;

						// Center this tooltip over the affected handles
						tooltips[handleNumber].innerHTML = poolValues[poolIndex].join(separator);
						tooltips[handleNumber].style.display = 'block';
						tooltips[handleNumber].style[direction] = offset + '%';
					} else {
						// Hide this tooltip
						tooltips[handleNumber].style.display = 'none';
					}
				}
			});
		});
	}
}
